import { useState } from "react";
import { useAdminData, type JournalEntry } from "./AdminDataContext";
import { useAdminAuth } from "./AdminAuthContext";
import { Plus, Pencil, Trash2, X, AlertTriangle, Eye, EyeOff, Loader2 } from "lucide-react";
import { ImageWithFallback } from "../figma/ImageWithFallback";
import { TranslationsPanel } from "./TranslationsPanel";
import { ImageUpload } from "./ImageUpload";

export function AdminJournal() {
  const { journal, addJournalEntry, updateJournalEntry, deleteJournalEntry, getNextJournalId, journalLoading } = useAdminData();
  const { hasPermission } = useAdminAuth();
  const [editing, setEditing] = useState<JournalEntry | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  if (!hasPermission("journal")) {
    return <p className="text-[#6F675F]" style={{ fontSize: "14px" }}>You don't have access to this section.</p>;
  }

  const openNew = () => {
    setIsNew(true);
    setEditing({
      id: getNextJournalId(),
      title: "",
      slug: "",
      excerpt: "",
      content: "",
      image: "",
      author: "",
      published: true,
      created_at: new Date().toISOString(),
    });
  };

  const handleSave = async () => {
    if (!editing) return;
    setSaveError(null);
    setSaving(true);
    const entry = {
      ...editing,
      slug: editing.slug || editing.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, ""),
    };
    try {
      const result = isNew ? await addJournalEntry(entry) : await updateJournalEntry(entry);
      if (result.error) {
        setSaveError(result.error);
        return;
      }
      setEditing(null);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1
          className="text-[#2D2A26] tracking-[0.08em]"
          style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "28px", fontWeight: 400 }}
        >
          Journal
        </h1>
        <button
          onClick={openNew}
          className="flex items-center gap-2 px-5 py-2.5 bg-[#6F7F63] text-white rounded-lg hover:bg-[#5F6F53] transition-colors tracking-[0.1em] uppercase"
          style={{ fontSize: "11px" }}
        >
          <Plus size={16} strokeWidth={1.5} />
          New Entry
        </button>
      </div>

      {journalLoading && (
        <p className="text-[#6F675F] mb-4" style={{ fontSize: "13px" }}>Loading journal…</p>
      )}

      {/* Entries */}
      <div className="space-y-4">
        {journal.map((entry) => (
          <div key={entry.id} className="bg-white rounded-xl overflow-hidden shadow-[0_2px_16px_rgba(40,32,20,0.04)] flex flex-col sm:flex-row">
            <div className="w-full sm:w-48 h-32 sm:h-auto bg-[#F2EEE8] shrink-0">
              {entry.image && (
                <ImageWithFallback src={entry.image} alt={entry.title} className="w-full h-full object-cover" />
              )}
            </div>
            <div className="flex-1 p-5">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h3 className="text-[#2D2A26]" style={{ fontSize: "16px" }}>{entry.title}</h3>
                  <p className="text-[#D8D1C7]" style={{ fontSize: "11px" }}>
                    {entry.author} &middot; {new Date(entry.created_at).toLocaleDateString()}
                  </p>
                </div>
                <span
                  className="flex items-center gap-1 px-2.5 py-1 rounded-full shrink-0"
                  style={{
                    background: entry.published ? "#74856D15" : "#D8D1C715",
                    color: entry.published ? "#74856D" : "#D8D1C7",
                    fontSize: "10px",
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                  }}
                >
                  {entry.published ? <Eye size={10} /> : <EyeOff size={10} />}
                  {entry.published ? "Published" : "Draft"}
                </span>
              </div>
              <p className="text-[#6F675F] mb-4" style={{ fontSize: "13px", fontWeight: 300 }}>{entry.excerpt}</p>
              <div className="flex gap-2">
                <button
                  onClick={() => { setIsNew(false); setEditing({ ...entry }); }}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-[#6F675F] hover:text-[#6F7F63] border border-[#E6E0D7] rounded-lg transition-colors"
                  style={{ fontSize: "11px" }}
                >
                  <Pencil size={12} /> Edit
                </button>
                <button
                  onClick={() => setDeleteConfirm(entry.id)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-[#6F675F] hover:text-[#8B5C58] border border-[#E6E0D7] rounded-lg transition-colors"
                  style={{ fontSize: "11px" }}
                >
                  <Trash2 size={12} /> Delete
                </button>
              </div>
            </div>
          </div>
        ))}
        {journal.length === 0 && (
          <div className="bg-white rounded-xl p-12 shadow-[0_2px_16px_rgba(40,32,20,0.04)] text-center">
            <p className="text-[#D8D1C7]" style={{ fontSize: "14px" }}>No journal entries yet.</p>
          </div>
        )}
      </div>

      {/* Delete confirm */}
      {deleteConfirm !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm px-4">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle size={20} className="text-[#8B5C58]" />
              <h3 className="text-[#2D2A26]" style={{ fontSize: "16px" }}>Delete Entry?</h3>
            </div>
            {deleteError && (
              <p className="text-[#8B5C58] mb-2" style={{ fontSize: "12px" }}>{deleteError}</p>
            )}
            <div className="flex gap-3">
              <button onClick={() => { setDeleteConfirm(null); setDeleteError(null); }} disabled={deleting} className="flex-1 py-2.5 border border-[#E6E0D7] text-[#6F675F] rounded-lg hover:bg-[#F2EEE8] transition-colors disabled:opacity-50" style={{ fontSize: "12px" }}>Cancel</button>
              <button
                onClick={async () => {
                  if (deleteConfirm === null) return;
                  setDeleteError(null);
                  setDeleting(true);
                  const result = await deleteJournalEntry(deleteConfirm);
                  setDeleting(false);
                  if (result.error) setDeleteError(result.error);
                  else setDeleteConfirm(null);
                }}
                disabled={deleting}
                className="flex-1 py-2.5 bg-[#8B5C58] text-white rounded-lg hover:bg-[#7A4B48] transition-colors disabled:opacity-70 flex items-center justify-center gap-2"
                style={{ fontSize: "12px" }}
              >
                {deleting ? <Loader2 size={14} className="animate-spin" /> : null}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit/Create Modal */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/30 backdrop-blur-sm overflow-y-auto py-8 px-4">
          <div className="bg-white rounded-xl w-full max-w-2xl shadow-2xl my-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E6E0D7]">
              <h2 className="text-[#2D2A26]" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "20px", fontWeight: 400 }}>
                {isNew ? "New Entry" : "Edit Entry"}
              </h2>
              <button onClick={() => setEditing(null)} className="text-[#D8D1C7] hover:text-[#2D2A26]"><X size={20} /></button>
            </div>
            <div className="px-6 py-5 space-y-4 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[#6F675F] tracking-[0.12em] uppercase mb-2" style={{ fontSize: "10px" }}>Title *</label>
                  <input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className="w-full px-3 py-2.5 bg-[#F7F5F1] border border-[#E6E0D7] rounded-lg text-[#2D2A26] focus:outline-none focus:border-[#C7A76C]" style={{ fontSize: "13px" }} />
                </div>
                <div>
                  <label className="block text-[#6F675F] tracking-[0.12em] uppercase mb-2" style={{ fontSize: "10px" }}>Slug</label>
                  <input value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} placeholder="auto-generated" className="w-full px-3 py-2.5 bg-[#F7F5F1] border border-[#E6E0D7] rounded-lg text-[#2D2A26] placeholder:text-[#D8D1C7] focus:outline-none focus:border-[#C7A76C]" style={{ fontSize: "13px" }} />
                </div>
              </div>
              <div>
                <label className="block text-[#6F675F] tracking-[0.12em] uppercase mb-2" style={{ fontSize: "10px" }}>Author</label>
                <input value={editing.author} onChange={(e) => setEditing({ ...editing, author: e.target.value })} className="w-full px-3 py-2.5 bg-[#F7F5F1] border border-[#E6E0D7] rounded-lg text-[#2D2A26] focus:outline-none focus:border-[#C7A76C]" style={{ fontSize: "13px" }} />
              </div>
              <ImageUpload
                label="Hero image"
                value={editing.image}
                onChange={(url) => setEditing({ ...editing, image: url })}
                previewAspect="aspect-video"
              />
              <div className="border-t border-[#E6E0D7] pt-4">
                <p className="text-[#6F675F] tracking-[0.12em] uppercase mb-3" style={{ fontSize: "10px" }}>English (default)</p>
                <p className="text-[#D8D1C7] mb-3" style={{ fontSize: "11px" }}>This is the original text used when no translation is set for a language.</p>
                <div className="space-y-3">
                  <div>
                    <label className="block text-[#6F675F] tracking-[0.12em] uppercase mb-1.5" style={{ fontSize: "10px" }}>Excerpt</label>
                    <textarea
                      rows={2}
                      value={editing.excerpt}
                      onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })}
                      placeholder="Short summary for listings and cards"
                      className="w-full px-3 py-2.5 bg-[#F7F5F1] border border-[#E6E0D7] rounded-lg text-[#2D2A26] placeholder:text-[#D8D1C7]/60 focus:outline-none focus:border-[#C7A76C] resize-none"
                      style={{ fontSize: "13px" }}
                    />
                  </div>
                  <div>
                    <label className="block text-[#6F675F] tracking-[0.12em] uppercase mb-1.5" style={{ fontSize: "10px" }}>Content</label>
                    <textarea
                      rows={6}
                      value={editing.content}
                      onChange={(e) => setEditing({ ...editing, content: e.target.value })}
                      placeholder="Full post body (English)"
                      className="w-full px-3 py-2.5 bg-[#F7F5F1] border border-[#E6E0D7] rounded-lg text-[#2D2A26] placeholder:text-[#D8D1C7]/60 focus:outline-none focus:border-[#C7A76C] resize-y min-h-[120px]"
                      style={{ fontSize: "13px" }}
                    />
                  </div>
                </div>
              </div>
              <TranslationsPanel
                fields={[
                  { key: "title", label: "Title", englishValue: editing.title },
                  { key: "excerpt", label: "Excerpt", type: "textarea", rows: 2, englishValue: editing.excerpt },
                  { key: "content", label: "Content", type: "textarea", rows: 6, englishValue: editing.content },
                ]}
                i18n={editing.i18n || {}}
                onChange={(i18n) => setEditing({ ...editing, i18n })}
              />
              <div>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editing.published}
                    onChange={(e) => setEditing({ ...editing, published: e.target.checked })}
                    className="w-4 h-4 rounded border-[#E6E0D7] text-[#6F7F63] focus:ring-[#6F7F63]"
                  />
                  <span className="text-[#2D2A26]" style={{ fontSize: "13px" }}>Published</span>
                </label>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-[#E6E0D7] flex flex-col gap-3">
              {saveError && (
                <p className="text-[#8B5C58]" style={{ fontSize: "12px" }}>{saveError}</p>
              )}
              <div className="flex justify-end gap-3">
                <button onClick={() => setEditing(null)} disabled={saving} className="px-5 py-2.5 border border-[#E6E0D7] text-[#6F675F] rounded-lg hover:bg-[#F2EEE8] transition-colors disabled:opacity-50" style={{ fontSize: "12px" }}>Cancel</button>
                <button onClick={handleSave} disabled={saving} className="px-5 py-2.5 bg-[#6F7F63] text-white rounded-lg hover:bg-[#5F6F53] transition-colors tracking-[0.1em] uppercase disabled:opacity-70 flex items-center gap-2" style={{ fontSize: "12px" }}>
                  {saving && <Loader2 size={14} className="animate-spin" />}
                  {isNew ? "Create" : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}